<template>
    <div class="bg-white">
        <component
            v-if="
                [
                    'cme',
                    'cold_probe',
                    'duration',
                    'preference_assessment',
                    'percent',
                    'multi_percent',
                    'multi_tally',
                ].includes(goal.tracker_type)
            "
            :is="`tracker-new-data-${goal.tracker_type.replace('_', '-')}`"
            :goal="goal"
            :customization="customization"
            :edit-target="editTarget"
            :saving="saving"
            @save-data="apiSaveData($event)"
            @edit-done="$emit('edit-done')"
        ></component>

        <template v-else="">
            <tracker-new-data-header
                v-if="!editTarget"
                :columns="headers[trackerMethodType]"
            ></tracker-new-data-header>

            <div style="border: solid 1px #8f8f8f;" v-if="canTrackManually">
                <div class="row px-3 new-data-inputs">
                    <div class="col-2 px-1">
                        <v-date-picker
                            dusk="date"
                            placeholder="MM/DD/YYYY"
                            id="date"
                            class="picker-container"
                            popover-align="left"
                            v-model="date"
                        ></v-date-picker>
                    </div>

                    <template v-if="['tally', 'time_sampling'].includes(trackerMethodType)">
                        <div class="p-1 col-2">
                            <input
                                type="number"
                                v-model.number="success"
                                dusk="success"
                                class="form-control form-control-sm"
                                name="count"
                            />
                        </div>
                        <div class="p-1 col-2">
                            {{ goal.parameters.success }}
                        </div>
                        <div class="p-1 col-5">
                            <input
                                v-model="note"
                                dusk="note"
                                class="form-control form-control-sm"
                                name="note"
                            />
                        </div>
                    </template>

                    <template v-if="['articulation'].includes(trackerMethodType)">
                        <div class="p-1 col-2">
                            <input
                                v-mask="'###'"
                                type="text"
                                v-model="success"
                                name="articulation-success"
                                class="custom-input"
                                style="max-width: 3rem;"
                            />%
                        </div>
                        <div class="col-7 p-1">
                            <input
                                v-model="note"
                                class="form-control form-control-sm"
                                name="note"
                            />
                        </div>
                    </template>

                    <template v-if="trackerMethodType === 'task_analysis'">
                        <div class="p-1 col-2">
                            <input
                                v-mask="'###'"
                                type="text"
                                v-model="success"
                                name="task-success"
                                class="custom-input"
                                style="max-width: 3rem;"
                            />
                        </div>
                        <div class="p-1 col-2">
                            {{ goal.parameters.steps.length }}
                        </div>
                        <div class="p-1 col-2">
                            {{
                                goal.parameters.steps.length
                                    ? parseInt((100 * success) / goal.parameters.steps.length)
                                    : 0
                            }}%
                        </div>
                        <div class="p-1 col-3">
                            <input
                                v-model="note"
                                class="form-control form-control-sm"
                                name="note"
                            />
                        </div>
                    </template>

                    <template v-if="['mand', 'mand_echoic'].includes(trackerMethodType)">
                        <div class="p-1 col-2">
                            <select v-model.number="success" class="custom-input" name="success">
                                <option v-for="x in possibleAttempts + 1">{{ x - 1 }}</option>
                            </select>
                        </div>
                        <div class="p-1 col-2">
                            {{ possibleAttempts }}
                        </div>
                        <div class="p-1 col-1">
                            {{ parseInt((100 * success) / possibleAttempts) }}%
                        </div>
                        <div class="p-1 col-4">
                            <input
                                v-model="note"
                                class="form-control form-control-sm"
                                name="note"
                            />
                        </div>
                    </template>

                    <tracker-new-data-actions
                        :edit-target="editTarget"
                        :saving="saving"
                        @save-data="submitTrackerData()"
                        @edit-done="$emit('edit-done')"
                    ></tracker-new-data-actions>
                </div>

                <div class="row px-3" v-if="serverErrors">
                    <div
                        class="col-12 p-1 d-flex justify-content-end"
                        v-for="error in serverErrors"
                    >
                        <span class="text-primary font-italic">{{ error }}<br /></span>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
<script>
import { mask } from "vue-the-mask";
import { fixDatePickers } from "../../plugins/fixDatePickers";
import TimeInput from "../ui/TimeInput";
import { permissions } from "../../plugins/permissions";
import TrackerNewDataActions from "./tracker-new-data/TrackerNewDataActions";
import TrackerNewDataHeader from "./tracker-new-data/TrackerNewDataHeader";
import TrackerNewDataCme from "./tracker-new-data/TrackerNewDataCme";
import TrackerNewDataColdProbe from "./tracker-new-data/TrackerNewDataColdProbe";
import TrackerNewDataPercent from "./tracker-new-data/TrackerNewDataPercent";
import TrackerNewDataDuration from "./tracker-new-data/TrackerNewDataDuration";
import TrackerNewDataMultiPercent from "./tracker-new-data/TrackerNewDataMultiPercent";
import TrackerNewDataMultiTally from "./tracker-new-data/TrackerNewDataMultiTally";
import TrackerNewDataPreferenceAssessment from "./tracker-new-data/TrackerNewDataPreferenceAssessment";

export default {
    name: "GoalsNewData",
    directives: { mask },
    components: {
        TrackerNewDataActions,
        TrackerNewDataHeader,
        TrackerNewDataCme,
        TrackerNewDataColdProbe,
        TrackerNewDataDuration,
        TrackerNewDataPercent,
        TrackerNewDataMultiPercent,
        TrackerNewDataMultiTally,
        TrackerNewDataPreferenceAssessment,
        TimeInput,
    },
    props: ["goal", "editTarget", "customization"],
    mixins: [fixDatePickers, permissions],
    data() {
        return {
            date: new Date(),
            success: 0,
            results: [],

            note: "",
            possibleAttempts: 1,
            attempts: 0,
            saving: false,

            serverErrors: [],
            headers: {
                tally: {
                    titles: [`Date`, `Count`, `Goal`, `Note`, ``],
                    col: [2, 2, 2, 5, 1],
                },
                time_sampling: {
                    titles: [`Date`, `Success`, `Goal`, `Note`, ``],
                    col: [2, 2, 2, 5, 1],
                },
                articulation: {
                    titles: [`Date`, `Percent`, `Note`, ``],
                    col: [2, 2, 7, 1],
                },
                task_analysis: {
                    titles: [`Date`, `Success`, `Attempts`, `% Correct`, `Note`, ``],
                    col: [2, 2, 2, 2, 3, 1],
                },
                mand: {
                    titles: [`Date`, `Success`, `Attempts`, `Percent`, `Note`, ``],
                    col: [2, 2, 2, 1, 4, 1],
                },
                mand_echoic: {
                    titles: [`Date`, `Success`, `Attempts`, `Percent`, `Note`, ``],
                    col: [2, 2, 2, 1, 4, 1],
                },
            },
        };
    },
    created() {
        this.initialize();
    },
    watch: {
        goal() {
            this.initialize();
            this.$bus.$emit("fix-pickers");
        },
        editTarget() {
            this.populateEdit();
        },
    },
    computed: {
        isoDate() {
            return this.moment(this.date, "MM-DD-YY").format("YYYY-MM-DD");
        },
        trackerMethodType() {
            if (this.goal.tracker_type === "preference_assessment") {
                return `preference_assessment_${this.goal.parameters.method}`;
            } else {
                return this.goal.tracker_type;
            }
        },
        endpoint() {
            if (this.editTarget) {
                return `/api/patients/${this.goal.patient.id}/goals/${this.goal.id}/trackers/${
                    this.editTarget.id
                }`;
            } else {
                return `/api/patients/${this.goal.patient.id}/goals/${this.goal.id}/trackers`;
            }
        },
    },
    methods: {
        /*** SETUP ***/

        async initialize() {
            if (this.goal.tracker_type === "percent") {
                this.possibleAttempts = parseInt(this.goal.parameters.attempts);
            }
            if (this.goal.tracker_type === "mand" || this.goal.tracker_type === "mand_echoic") {
                this.possibleAttempts = this.goal.parameters.mand_opportunities;
            }
            if (this.goal.tracker_type === "preference_assessment") {
            }
            this.populateEdit();
        },

        populateEdit() {
            if (this.editTarget) {
                this.date = this.editTarget.session_at;
                this.note = this.editTarget.notes;
                switch (this.goal.tracker_type) {
                    case "tally":
                    case "percent":
                    case "articulation":
                    case "mand":
                    case "mand_echoic":
                    case "time_sampling":
                        this.success = this.editTarget.data.success;
                        break;
                }
                setTimeout(this.removePickerMinWidth, 300);
            }
        },

        /*** TRACKERS ***/

        submitTallyTimeSampling() {
            this.serverErrors = [];
            const payload = {
                data: {
                    type: this.goal.tracker_type,
                    success: this.success,
                    goal: this.goal.parameters.success,
                },
                notes: this.note,
                session_at: this.isoDate,
            };

            this.apiSaveData(payload);
        },

        submitPercent() {
            const payload = {
                data: {
                    type: "percent",
                    success: parseInt(this.success),
                    attempts: this.possibleAttempts,
                },
                notes: this.note,
                session_at: this.isoDate,
            };

            this.apiSaveData(payload);
        },

        submitMand() {
            let results = [];

            if (this.goal.tracker_type === "mand_echoic") {
                let successfulMandArray = Array(this.goal.parameters.echoic_repetitions).fill(1);
                let unsuccessfulMandArray = Array(this.goal.parameters.echoic_repetitions).fill(0);
                for (let i = 0; i < parseInt(this.goal.parameters.mand_opportunities); i++) {
                    if (i < this.success) {
                        results.push({ results: successfulMandArray });
                    } else {
                        results.push({ results: unsuccessfulMandArray });
                    }
                }
            } else {
                for (let i = 0; i < parseInt(this.success); i++) {
                    results.push({ result: 1 });
                }
                for (let i = 0; i < parseInt(this.possibleAttempts - this.success); i++) {
                    results.push({ result: 0 });
                }
            }

            const payload = {
                data: {
                    type: this.goal.tracker_type,
                    success: parseInt(this.success),
                    attempts: this.possibleAttempts,
                    results: results,
                },
                notes: this.note,
                is_manual_entry: true,
                session_at: this.isoDate,
            };

            this.apiSaveData(payload);
        },

        submitArticulation() {
            const payload = {
                data: {
                    type: "articulation",
                    success: parseInt(this.success),
                },
                notes: this.note,
                session_at: this.isoDate,
            };

            this.apiSaveData(payload);
        },

        submitTaskAnalysis() {
            const payload = {
                data: {
                    type: "task_analysis",
                    results: [],
                    success: this.goal.parameters.steps.length
                        ? parseInt((100 * this.success) / this.goal.parameters.steps.length)
                        : 0,
                },
                notes: this.note,
                session_at: this.isoDate,
            };

            this.apiSaveData(payload);
        },

        /*** SAVING AND ERROR TREATMENT METHODS ***/

        submitTrackerData() {
            switch (this.goal.tracker_type) {
                case "tally":
                case "time_sampling":
                    this.submitTallyTimeSampling();
                    break;
                case "multi_percent":
                    this.submitMultiPercent();
                    break;
                case "percent":
                    this.submitPercent();
                    break;
                case "mand":
                case "mand_echoic":
                    this.submitMand();
                    break;
                case "articulation":
                    this.submitArticulation();
                    break;
                case "task_analysis":
                    this.submitTaskAnalysis();
                    break;
            }
        },

        apiSaveData(payload) {
            this.serverErrors = [];
            this.saving = true;
            payload.is_manual_entry = 1;
            axios({
                method: this.editTarget ? "patch" : "post",
                url: this.endpoint,
                data: payload,
            })
                .then(r => {
                    this.successfulSave(r);
                })
                .catch(e => {
                    this.unsuccessfulSave(e);
                });
        },

        emitRefreshEvents() {
            this.$emit("refresh-goals");
            this.$emit("refresh-trackers");
            this.$emit("edit-done");
        },

        resetComponent() {
            this.date = new Date();
            this.success = 0;
            this.score = null;
            this.scores = [];
            this.note = "";
            this.possibleAttempts = 1;
            this.attempts = 0;
            this.saving = false;
            this.results = [];

            if (this.goal.tracker_type === "percent") {
                this.possibleAttempts = parseInt(this.goal.parameters.attempts);
            } else if (
                this.goal.tracker_type === "mand" ||
                this.goal.tracker_type === "mand_echoic"
            ) {
                this.possibleAttempts = parseInt(this.goal.parameters.mand_opportunities);
            }

            this.emitRefreshEvents();
        },

        successfulSave(r) {
            this.saving = false;
            this.resetComponent();
            if (!this.editTarget) this.mixpanelTrack(this.goal.tracker_type);
        },

        unsuccessfulSave(e) {
            console.error(e);
            if (_.get(e, "response.status", null) === 403) {
                this.$swal({
                    title: "Error",
                    text: `This goal was created by ${_.get(
                        this.goal,
                        "created_by_user.name",
                        "another user"
                    )}, you are not authorized to edit it. `,
                    type: "error",
                    confirmButtonText: "Close",
                    confirmButtonColor: "#d64571",
                });
            } else {
                this.$swal({
                    title: "Error",
                    text: "An error occurred trying to edit this goal.",
                    type: "error",
                    confirmButtonText: "Close",
                    confirmButtonColor: "#d64571",
                });
            }
            this.parseServerErrors(e.response.data.errors);
            this.saving = false;
        },

        parseServerErrors(errors) {
            // loop through all errors
            //  errors variable is an object/dictionary
            let results = [];
            for (let key in errors) {
                let errorsForType = errors[key];
                // The errors from the servers may or may not be in the form of an array,
                // so we need to "normalize" them by simply looping through the list
                // and adding each error as a simple string.
                if (Array.isArray(errorsForType)) {
                    for (let errorArray of errorsForType) {
                        if (Array.isArray(errorArray)) {
                            // each type validation type can have multiple reasons for failing
                            for (let e of errorArray) {
                                this.serverErrors.push(e);
                            }
                        } else {
                            // handles the case when error is unknown
                            this.serverErrors.push(errorArray);
                        }
                    }
                } else {
                    this.serverErrors.push(errorsForType);
                }
            }
        },

        mixpanelTrack(method) {
            mixpanel.track("Data Trackers - Manual Entry", {
                Method: method,
            });
        },
    },
};
</script>
<style scoped lang="scss">
.new-data-inputs {
    > .col-1,
    .col-2,
    .col-,
    .col-4 {
        display: flex;
        align-items: center;
    }
}
</style>
